import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View, Pressable, Animated, Easing, ScrollView, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Tasks hero demos (tasks page) — animated “Watch it happen” + week calendar.
// Discovery copy + chips + search live in `CatalogDiscoveryHero` on the page.

// Shared storyboard — both animated heroes hit these beats in unison.
// Total cycle: 10s.
const STORY = {
  PRE_ROLL:        300,    // 0.0  → 0.3   quiet pre-roll
  BUILD_START:     300,    // 0.3  → 2.1   build setup rows / drop in chips
  BUILD_END:       2100,
  BEAT_END:        2600,   // 2.1  → 2.6   anticipation pause
  TRANSITION_END:  2960,   // 2.6  → 2.96  divider / playhead reveal (360ms)
  EXECUTE_END:     7160,   // 2.96 → 7.16  run-to-deliver / sweep (4200ms)
  CELEBRATE_END:   7560,   // 7.16 → 7.56  celebration moment (400ms)
  HOLD_END:        9100,   // 7.56 → 9.10  hold to read (1540ms)
  EXIT_END:        9700,   // 9.10 → 9.70  fade out (600ms)
  CYCLE:           10000,  // 9.70 → 10.0  reset pause (300ms)
};

// Smooth, premium-feeling easing curves shared across both heroes.
const EASE_OUT  = Easing.bezier(0.22, 1, 0.36, 1);    // gentle landing
const EASE_IN   = Easing.bezier(0.55, 0, 1, 0.45);    // departing pull-out
const EASE_IO   = Easing.bezier(0.65, 0, 0.35, 1);    // smooth sweep
const EASE_SOFT = Easing.bezier(0.4, 0, 0.2, 1);      // material standard

// Week-calendar demo task pills inside the grid cells.
const CALENDAR_TASK_CHIP_HEIGHT = 22;

// Helpers — keep animation declarations terse and consistent.
const tFade = (val, to, duration, easing = EASE_OUT) =>
  Animated.timing(val, { toValue: to, duration, easing, useNativeDriver: true });

const wait = (ms) => Animated.delay(Math.max(0, ms));

// Tints used across the demos. Kept here so hero 15 and hero 16 share a palette.
const TINT = {
  blue:   '#3b82f6',
  purple: '#8b5cf6',
  green:  '#10b981',
  amber:  '#f59e0b',
  red:    '#ef4444',
  pink:   '#ec4899',
  cyan:   '#06b6d4',
};

// ─── Hero 15 — task examples that cycle each loop ───────────────────────────
// Each example shows a different real-world use of tasks, so the demo doesn't
// just teach "what is a task" but also "what could mine do?"
//
// Delivery is intentionally limited to the two channels Gab AI actually
// ships today: email, and saving the run as a Gab AI chat in the user's
// history. Don't add Slack / push / SMS / webhook examples here — they'd
// promise features we don't have.
const TASK_EXAMPLES = [
  {
    header: 'MORNING AI BRIEFING · NEW TASK',
    instruction: 'Brief me on overnight AI news',
    schedule: 'Daily · 8:00 AM',
    delivery: 'Email me the summary',
    deliveryIcon: 'envelope',
    dividerLabel: 'Tomorrow · 8:00 AM',
    statusTime: '8:00 AM',
    deliveredLabel: '8:00 AM · delivered',
    resultBadge: 'INBOX',
    resultSubject: 'Your morning AI brief — Top 3 stories',
    resultBullets: [
      'OpenAI ships o3 reasoning mode to all Plus users.',
      'Anthropic releases Claude Code 3.0 with project memory.',
      'Apple integrates on-device Llama into the next iOS beta.',
    ],
  },
  {
    header: 'WEEKLY SALES DIGEST · NEW TASK',
    instruction: "Summarize last week's sales numbers",
    schedule: 'Mondays · 7:00 AM',
    delivery: 'Save to my Gab AI chats',
    deliveryIcon: 'comments',
    dividerLabel: 'Monday · 7:00 AM',
    statusTime: '7:00 AM',
    deliveredLabel: '7:00 AM · saved',
    resultBadge: 'CHAT',
    resultSubject: 'Last week — Three things to know',
    resultBullets: [
      'Revenue up 12% week-over-week, led by enterprise.',
      'Demo conversion held at 34% across 47 calls.',
      'Three deals slipped to next week — all >$50k ACV.',
    ],
  },
  {
    header: 'NIGHTLY JOURNAL PROMPT · NEW TASK',
    instruction: 'Send me a thoughtful question',
    schedule: 'Daily · 9:00 PM',
    delivery: 'Email me the prompt',
    deliveryIcon: 'envelope',
    dividerLabel: 'Tonight · 9:00 PM',
    statusTime: '9:00 PM',
    deliveredLabel: '9:00 PM · delivered',
    resultBadge: 'INBOX',
    resultSubject: "Tonight's journal — pick one",
    resultBullets: [
      'What surprised you today, even a little?',
      "Who's one person you want to thank this week?",
      "What's one thing you'd do differently tomorrow?",
    ],
  },
];

// ─── Hero 16 — week variations that cycle each loop ─────────────────────────
// Each "week" represents a different persona. Same number of tasks (10) so the
// animation timing stays in lock-step with hero 15's 10-second cycle.
const WEEKS = [
  // Week 1 — "Always informed" — daily news + journals + Friday recap
  [
    { col: 0, row: 0, type: 'brief' },
    { col: 0, row: 3, type: 'journal' },
    { col: 1, row: 0, type: 'brief' },
    { col: 1, row: 1, type: 'lunch' },
    { col: 2, row: 0, type: 'brief' },
    { col: 2, row: 3, type: 'journal' },
    { col: 3, row: 0, type: 'brief' },
    { col: 3, row: 1, type: 'lunch' },
    { col: 4, row: 0, type: 'brief' },
    { col: 4, row: 2, type: 'recap' },
  ],
  // Week 2 — "Athlete & operator" — daily workouts + meeting prep + recap
  [
    { col: 0, row: 0, type: 'workout' },
    { col: 0, row: 2, type: 'prep' },
    { col: 1, row: 0, type: 'workout' },
    { col: 1, row: 1, type: 'lunch' },
    { col: 2, row: 0, type: 'workout' },
    { col: 2, row: 2, type: 'prep' },
    { col: 3, row: 0, type: 'workout' },
    { col: 3, row: 3, type: 'journal' },
    { col: 4, row: 0, type: 'workout' },
    { col: 4, row: 2, type: 'recap' },
  ],
  // Week 3 — "Standup-led" — daily standups + lunch reads + Friday journal
  [
    { col: 0, row: 0, type: 'standup' },
    { col: 0, row: 1, type: 'lunch' },
    { col: 1, row: 0, type: 'standup' },
    { col: 1, row: 3, type: 'journal' },
    { col: 2, row: 0, type: 'standup' },
    { col: 2, row: 2, type: 'prep' },
    { col: 3, row: 0, type: 'standup' },
    { col: 3, row: 1, type: 'lunch' },
    { col: 4, row: 0, type: 'standup' },
    { col: 4, row: 3, type: 'journal' },
  ],
];

function Spinner({ color, size = 12 }) {
  const spin = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(spin, { toValue: 1, duration: 900, useNativeDriver: true })
    );
    loop.start();
    return () => loop.stop();
  }, [spin]);
  const rotate = spin.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });
  return (
    <Animated.View style={{ width: size, height: size, transform: [{ rotate }] }}>
      <Icon name="circle-notch" size={size} color={color} solid />
    </Animated.View>
  );
}

function WatchItHappenHero() {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();

  const accent = colors.accent_color_primary;
  const blue = TINT.blue;
  const purple = TINT.purple;
  const green = TINT.green;

  const [exampleIdx, setExampleIdx] = useState(0);
  const example = TASK_EXAMPLES[exampleIdx];

  const instrOp = useRef(new Animated.Value(0)).current;
  const schedOp = useRef(new Animated.Value(0)).current;
  const delivOp = useRef(new Animated.Value(0)).current;
  const dividerOp = useRef(new Animated.Value(0)).current;
  const runningOp = useRef(new Animated.Value(0)).current;
  const checkScale = useRef(new Animated.Value(0)).current;
  const resultOp = useRef(new Animated.Value(0)).current;
  const resultY = useRef(new Animated.Value(12)).current;
  const resultGlow = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let cancelled = false;

    const reset = () => {
      instrOp.setValue(0);
      schedOp.setValue(0);
      delivOp.setValue(0);
      dividerOp.setValue(0);
      runningOp.setValue(0);
      checkScale.setValue(0);
      resultOp.setValue(0);
      resultY.setValue(12);
      resultGlow.setValue(0);
    };

    // Hero 15 storyboard (offsets in ms from cycle start):
    //  0.30 → 1.70   build the three setup rows (3 × 360 + 2 × 160)
    //  2.10 → 2.60   anticipation pause
    //  2.60 → 2.96   divider fades in
    //  2.96 → 3.32   spinner row appears
    //  3.32 → 5.40   "running…" holds (~2080ms of activity)
    //  5.40 → 5.78   spinner cross-fades to checkmark
    //  5.80 → 6.42   result slides up + glow ramps up
    //  6.42 → 7.16   glow ramps back down
    //  7.16 → 9.10   result holds (the result IS the celebration here)
    //  9.10 → 9.70   exit fade everything (600ms)
    const SETUP_FADE = 360;
    const SETUP_GAP  = 160;
    const TRANSITION_DUR = STORY.TRANSITION_END - STORY.BEAT_END;   // 360
    const EXIT_DUR       = STORY.EXIT_END - STORY.HOLD_END;         // 600
    const RESET_DUR      = STORY.CYCLE - STORY.EXIT_END;            // 300

    reset();

    Animated.sequence([
      // BUILD — three rows assemble (0.30 → ~1.70s)
      wait(STORY.BUILD_START),
      tFade(instrOp, 1, SETUP_FADE),
      wait(SETUP_GAP),
      tFade(schedOp, 1, SETUP_FADE),
      wait(SETUP_GAP),
      tFade(delivOp, 1, SETUP_FADE),

      // BEAT — pause so the assembled task can be read (~2.10 → 2.60)
      wait(STORY.BEAT_END - (STORY.BUILD_START + 3 * SETUP_FADE + 2 * SETUP_GAP)),

      // TRANSITION — divider fades in (2.60 → 2.96)
      tFade(dividerOp, 1, TRANSITION_DUR),

      // EXECUTE — spinner appears, holds, then becomes checkmark (4200ms total)
      tFade(runningOp, 1, 360),
      wait(2080),
      Animated.parallel([
        tFade(runningOp, 0, 280, EASE_IN),
        Animated.spring(checkScale, {
          toValue: 1,
          friction: 6,
          tension: 130,
          useNativeDriver: true,
        }),
      ]),
      wait(40),
      Animated.parallel([
        tFade(resultOp, 1, 620),
        Animated.timing(resultY, {
          toValue: 0,
          duration: 620,
          easing: EASE_OUT,
          useNativeDriver: true,
        }),
        Animated.sequence([
          tFade(resultGlow, 1, 380),
          tFade(resultGlow, 0, 740, EASE_IN),
        ]),
      ]),

      // CELEBRATE + HOLD — result holds (~7.16 → 9.10)
      wait(STORY.HOLD_END - STORY.EXECUTE_END),

      // EXIT — fade everything (9.10 → 9.70)
      Animated.parallel([
        tFade(resultOp, 0, EXIT_DUR, EASE_IN),
        tFade(checkScale, 0, EXIT_DUR, EASE_IN),
        tFade(dividerOp, 0, EXIT_DUR, EASE_IN),
        tFade(schedOp, 0, EXIT_DUR, EASE_IN),
        tFade(delivOp, 0, EXIT_DUR, EASE_IN),
        tFade(instrOp, 0, EXIT_DUR, EASE_IN),
      ]),

      // RESET — pause before next cycle
      wait(RESET_DUR),
    ]).start(({ finished }) => {
      if (!cancelled && finished) {
        // Advance to the next example. The effect re-runs with the new copy
        // and animation values stay at 0 from the EXIT phase, so the swap
        // is invisible to the user.
        setExampleIdx(i => (i + 1) % TASK_EXAMPLES.length);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    exampleIdx,
    instrOp, schedOp, delivOp, dividerOp,
    runningOp, checkScale, resultOp, resultY, resultGlow,
  ]);

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      marginBottom: 0,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      overflow: 'hidden',
    },
    inner: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      padding: isXS ? 12 : 14
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    },
    headerCopy: { flex: 1, minWidth: 220, gap: 2 },
    eyebrow: {
      letterSpacing: 1.4,
      textTransform: 'uppercase',
    },
    title: {
      fontSize: isXS ? 17 : 19,
      letterSpacing: -0.4,
      lineHeight: isXS ? 22 : 24,
    },
    subtitle: { lineHeight: 18 },

    demoCard: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
    },
    demoCardHeader: {
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border_color_secondary,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    demoBody: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      padding: isXS ? 10 : 12,
      gap: 6,
    },

    setupRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 9,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 9,
      backgroundColor: colors.solid_color_primary,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border_color_secondary,
    },
    setupIconWrap: {
      width: 22, height: 22, borderRadius: 7,
      alignItems: 'center', justifyContent: 'center',
    },
    setupLabel: {
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      lineHeight: 12,
      marginBottom: 1,
    },

    dividerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginVertical: 1,
    },
    dividerLine: {
      flex: 1, height: StyleSheet.hairlineWidth,
      backgroundColor: colors.border_color_secondary,
    },
    dividerText: {
      letterSpacing: 1, textTransform: 'uppercase',
    },

    statusRow: {
      position: 'absolute',
      left: 0, right: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 9,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 9,
      backgroundColor: `${accent}10`,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${accent}33`,
    },
    statusIconWrap: {
      width: 20, height: 20, borderRadius: 10,
      alignItems: 'center', justifyContent: 'center',
    },
    statusContainer: {
      height: 34,
      position: 'relative',
    },

    resultCard: {
      borderRadius: 10,
      borderWidth: 1,
      borderColor: `${green}55`,
      backgroundColor: colors.solid_color_primary,
      padding: 10,
      gap: 5,
      shadowColor: green,
      shadowOpacity: 0.18,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 4 },
    },
    resultGlowOverlay: {
      position: 'absolute',
      top: -4, left: -4, right: -4, bottom: -4,
      borderRadius: 12,
      borderWidth: 2,
      borderColor: green,
    },
    resultHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    resultBadge: {
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 4,
      backgroundColor: `${green}26`,
    },
    resultSubject: {
      fontSize: isXS ? 13 : 14,
      lineHeight: isXS ? 17 : 19,
      marginTop: 1,
    },
    resultBullet: {
      flexDirection: 'row',
      gap: 8,
      paddingLeft: 2,
    },
    resultBulletDot: {
      color: colors.text_color_tertiary,
      lineHeight: 17,
      fontSize: 12,
    },
  }), [colors, isXS, accent, green]);

  return (
    <View style={styles.wrap}>
      <LinearGradient
        colors={[`${accent}10`, 'transparent']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.inner}>
        <View style={styles.headerRow}>
          <View style={styles.headerCopy}>
            <Text size="xs" weight="bold" color="secondary" style={styles.eyebrow}>
              ① One task
            </Text>
            <Text weight="extraBold" style={styles.title}>
              Here's how one works.
            </Text>
            <Text size="n" color="secondary" style={[styles.subtitle, { paddingBottom: 5 }]}>
              Tell it what you want — and when. It handles the rest.
            </Text>
          </View>
        </View>

        <View style={styles.demoCard}>
          <View style={styles.demoCardHeader}>
            <View style={{ flexDirection: 'row', gap: 5 }}>
              <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#ef4444' }} />
              <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#f59e0b' }} />
              <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#22c55e' }} />
            </View>
            <Text size="xxs" weight="bold" color="tertiary" style={{ letterSpacing: 0.8, marginLeft: 6 }}>
              {example.header}
            </Text>
          </View>

          <View style={styles.demoBody}>
            <Animated.View style={[styles.setupRow, { opacity: instrOp }]}>
              <View style={[styles.setupIconWrap, { backgroundColor: `${blue}26` }]}>
                <Icon name="message" size={11} color={blue} solid />
              </View>
              <View style={{ flex: 1 }}>
                <Text size="xxs" weight="bold" color="tertiary" style={styles.setupLabel}>
                  Instruction
                </Text>
                <Text size="xs" weight="bold" numberOfLines={1}>
                  {example.instruction}
                </Text>
              </View>
            </Animated.View>

            <Animated.View style={[styles.setupRow, { opacity: schedOp }]}>
              <View style={[styles.setupIconWrap, { backgroundColor: `${purple}26` }]}>
                <Icon name="clock" size={11} color={purple} solid />
              </View>
              <View style={{ flex: 1 }}>
                <Text size="xxs" weight="bold" color="tertiary" style={styles.setupLabel}>
                  Schedule
                </Text>
                <Text size="xs" weight="bold">{example.schedule}</Text>
              </View>
            </Animated.View>

            <Animated.View style={[styles.setupRow, { opacity: delivOp }]}>
              <View style={[styles.setupIconWrap, { backgroundColor: `${green}26` }]}>
                <Icon name={example.deliveryIcon} size={11} color={green} solid />
              </View>
              <View style={{ flex: 1 }}>
                <Text size="xxs" weight="bold" color="tertiary" style={styles.setupLabel}>
                  Delivery
                </Text>
                <Text size="xs" weight="bold">{example.delivery}</Text>
              </View>
            </Animated.View>

            <Animated.View style={[styles.dividerRow, { opacity: dividerOp }]}>
              <View style={styles.dividerLine} />
              <Text size="xxs" weight="bold" color="tertiary" style={styles.dividerText}>
                {example.dividerLabel}
              </Text>
              <View style={styles.dividerLine} />
            </Animated.View>

            <View style={styles.statusContainer}>
              <Animated.View style={[styles.statusRow, { opacity: runningOp }]}>
                <View style={[styles.statusIconWrap, { backgroundColor: `${accent}26` }]}>
                  <Spinner color={accent} size={10} />
                </View>
                <Text size="xs" weight="bold">Running task…</Text>
                <Text size="xxs" color="secondary" style={{ marginLeft: 'auto' }}>
                  {example.statusTime}
                </Text>
              </Animated.View>

              <Animated.View
                style={[
                  styles.statusRow,
                  {
                    backgroundColor: `${green}14`,
                    borderColor: `${green}44`,
                    opacity: checkScale,
                    transform: [{ scale: checkScale.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] }) }],
                  },
                ]}
              >
                <View style={[styles.statusIconWrap, { backgroundColor: `${green}33` }]}>
                  <Icon name="check" size={10} color={green} solid />
                </View>
                <Text size="xs" weight="bold">Done</Text>
                <Text size="xxs" color="secondary" style={{ marginLeft: 'auto' }}>
                  {example.deliveredLabel}
                </Text>
              </Animated.View>
            </View>

            <Animated.View
              style={{
                opacity: resultOp,
                transform: [{ translateY: resultY }],
              }}
            >
              <View style={styles.resultCard}>
                <Animated.View
                  style={[styles.resultGlowOverlay, { opacity: resultGlow }]}
                  pointerEvents="none"
                />
                <View style={styles.resultHeader}>
                  <View style={styles.resultBadge}>
                    <Text size="xxs" weight="bold" style={{ color: green, letterSpacing: 0.5 }}>
                      {example.resultBadge}
                    </Text>
                  </View>
                  <Text size="xxs" color="tertiary">From Gab AI · just now</Text>
                </View>
                <Text weight="bold" style={styles.resultSubject}>
                  {example.resultSubject}
                </Text>
                {example.resultBullets.map((bullet, i) => (
                  <View key={i} style={styles.resultBullet}>
                    <Text size="xs" style={styles.resultBulletDot}>{i + 1}.</Text>
                    <Text size="xs" color="secondary" style={{ flex: 1, lineHeight: 17 }}>
                      {bullet}
                    </Text>
                  </View>
                ))}
              </View>
            </Animated.View>
          </View>
        </View>
      </View>
    </View>
  );
}

function YourWeekHandledHero() {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const accent = colors.accent_color_primary;

  const gutterW = isXS ? 28 : 36;

  const TYPES = useMemo(() => ({
    brief:    { name: 'AI Brief',     icon: 'newspaper',     tint: TINT.blue   },
    standup:  { name: 'Standup',      icon: 'list-check',    tint: TINT.cyan   },
    lunch:    { name: 'Lunch Read',   icon: 'mug-hot',       tint: TINT.amber  },
    workout:  { name: 'Workout',      icon: 'dumbbell',      tint: TINT.red    },
    prep:     { name: 'Meeting Prep', icon: 'calendar-day',  tint: TINT.pink   },
    recap:    { name: 'Weekly Recap', icon: 'chart-line',    tint: TINT.green  },
    journal:  { name: 'Journal',      icon: 'feather',       tint: TINT.purple },
  }), []);

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  const ROWS = [
    { label: '8a',  hour: 8 },
    { label: '12p', hour: 12 },
    { label: '5p',  hour: 17 },
    { label: '9p',  hour: 21 },
  ];

  const [weekIdx, setWeekIdx] = useState(0);
  const TASKS = WEEKS[weekIdx];

  // Always allocate animation refs for the largest possible week so we can
  // swap data without recreating refs. Every week happens to have the same
  // count today, but this keeps the contract loose.
  const MAX_TASKS = useMemo(
    () => WEEKS.reduce((m, w) => Math.max(m, w.length), 0),
    []
  );
  const taskAnims = useRef(
    Array.from({ length: MAX_TASKS }, () => ({
      enter: new Animated.Value(0),
      glow: new Animated.Value(0),
      delivered: new Animated.Value(0),
    }))
  ).current;

  const playhead = useRef(new Animated.Value(0)).current;
  const playheadOpacity = useRef(new Animated.Value(0)).current;
  const completionOp = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let cancelled = false;

    const reset = () => {
      taskAnims.forEach(a => {
        a.enter.setValue(0);
        a.glow.setValue(0);
        a.delivered.setValue(0);
      });
      playhead.setValue(0);
      playheadOpacity.setValue(0);
      completionOp.setValue(0);
    };

    // Hero 16 storyboard (offsets in ms from cycle start) — matches Hero 15:
    //  0.30 → ~2.10  chips drop into the calendar (staggered springs)
    //  2.10 → 2.60   anticipation pause
    //  2.60 → 2.96   playhead fades in at the left edge
    //  2.96 → 7.16   playhead sweeps Mon → Fri, lighting tasks as it passes
    //  7.16 → 7.56   completion banner appears (playhead fades simultaneously)
    //  7.56 → 9.10   banner holds (read time)
    //  9.10 → 9.70   exit fade everything (600ms)
    const SWEEP_MS       = STORY.EXECUTE_END - STORY.TRANSITION_END;  // 4200
    const TRANSITION_DUR = STORY.TRANSITION_END - STORY.BEAT_END;     // 360
    const CELEBRATE_DUR  = STORY.CELEBRATE_END - STORY.EXECUTE_END;   // 400
    const HOLD_DUR       = STORY.HOLD_END - STORY.CELEBRATE_END;      // 1540
    const EXIT_DUR       = STORY.EXIT_END - STORY.HOLD_END;           // 600
    const RESET_DUR      = STORY.CYCLE - STORY.EXIT_END;              // 300

    // Stagger chips evenly across the build window. Sized so the build
    // duration stays ≈ 1670ms regardless of which week is showing — the
    // narrative beat boundary doesn't shift.
    const taskCount = TASKS.length;
    const CHIP_STAGGER = taskCount > 1 ? 1170 / (taskCount - 1) : 0;
    const BUILD_DURATION = (taskCount - 1) * CHIP_STAGGER + 500;

    reset();

    const entries = TASKS.map((_, i) =>
      Animated.sequence([
        wait(i * CHIP_STAGGER),
        Animated.spring(taskAnims[i].enter, {
          toValue: 1,
          friction: 7,
          tension: 110,
          useNativeDriver: true,
        }),
      ])
    );

    // Each pulse fires when the playhead reaches that column's center.
    const pulses = TASKS.map((task, i) => {
      const a = taskAnims[i];
      const colCenter = (task.col + 0.5) / DAYS.length;
      return Animated.sequence([
        wait(SWEEP_MS * colCenter),
        Animated.parallel([
          Animated.sequence([
            tFade(a.glow, 1, 240, EASE_OUT),
            tFade(a.glow, 0, 760, EASE_IN),
          ]),
          tFade(a.delivered, 1, 360, EASE_OUT),
        ]),
      ]);
    });

    Animated.sequence([
      // BUILD — chips drop in (0.30 → ~2.10)
      wait(STORY.BUILD_START),
      Animated.parallel(entries),

      // BEAT — pause so the assembled week can register
      wait(STORY.BEAT_END - STORY.BUILD_START - BUILD_DURATION),

      // TRANSITION — playhead reveal (2.60 → 2.96)
      tFade(playheadOpacity, 1, TRANSITION_DUR),

      // EXECUTE — sweep Mon → Fri (2.96 → 7.16)
      Animated.parallel([
        Animated.timing(playhead, {
          toValue: 1,
          duration: SWEEP_MS,
          easing: EASE_IO,
          useNativeDriver: true,
        }),
        Animated.parallel(pulses),
      ]),

      // CELEBRATE — playhead fades, banner rises (7.16 → 7.56)
      Animated.parallel([
        tFade(playheadOpacity, 0, CELEBRATE_DUR, EASE_IN),
        tFade(completionOp, 1, CELEBRATE_DUR),
      ]),

      // HOLD — banner reads (7.56 → 9.10)
      wait(HOLD_DUR),

      // EXIT — fade everything (9.10 → 9.70)
      Animated.parallel([
        tFade(completionOp, 0, EXIT_DUR, EASE_IN),
        ...TASKS.map((_, i) => tFade(taskAnims[i].enter, 0, EXIT_DUR, EASE_IN)),
        ...TASKS.map((_, i) => tFade(taskAnims[i].delivered, 0, EXIT_DUR, EASE_IN)),
      ]),

      // RESET — pause before next cycle
      wait(RESET_DUR),
    ]).start(({ finished }) => {
      if (!cancelled && finished) {
        // Advance to the next week. Effect re-runs with new TASKS; chips
        // are already invisible from the EXIT phase, so the swap is silent.
        setWeekIdx(i => (i + 1) % WEEKS.length);
      }
    });

    return () => { cancelled = true; };
  }, [weekIdx, taskAnims, playhead, playheadOpacity, completionOp, TASKS, DAYS.length]);

  const [gridWidth, setGridWidth] = useState(0);

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      marginBottom: 0,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      overflow: 'hidden',
    },
    inner: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      padding: isXS ? 12 : 14,
      gap: 10
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    },
    headerCopy: { flex: 1, minWidth: 220, gap: 2 },
    eyebrow: { letterSpacing: 1.4, textTransform: 'uppercase' },
    title: {
      fontSize: isXS ? 17 : 19,
      letterSpacing: -0.4,
      lineHeight: isXS ? 22 : 24,
    },
    subtitle: { lineHeight: 18 },

    calendarCard: {
      ...(Platform.OS === 'macos' ? { flexGrow: 1 } : { flex: 1 }),
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: isXS ? 8 : 10,
      gap: 6,
      overflow: 'hidden',
    },

    dayHeaderRow: { flexDirection: 'row', alignItems: 'center' },
    timeGutter: { width: gutterW },
    dayHeaderCell: { flex: 1, alignItems: 'center', paddingVertical: 2 },
    dayLabel: { letterSpacing: 1, textTransform: 'uppercase' },

    gridArea: { ...(Platform.OS === 'macos' ? { height: 220 } : { flex: 1 }), position: 'relative' },
    gridRow: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'stretch',
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border_color_secondary,
    },
    timeLabelCell: {
      width: gutterW,
      paddingVertical: 6,
      alignItems: 'flex-end',
      paddingRight: 4,
    },
    timeLabel: { letterSpacing: 0.5 },
    dayCell: {
      flex: 1,
      paddingVertical: isXS ? 4 : 5,
      paddingHorizontal: isXS ? 2 : 4,
      minHeight: isXS ? 30 : 34,
      borderLeftWidth: StyleSheet.hairlineWidth,
      borderLeftColor: colors.border_color_secondary,
    },

    chip: {
      height: CALENDAR_TASK_CHIP_HEIGHT,
      minHeight: CALENDAR_TASK_CHIP_HEIGHT,
      borderRadius: 6,
      paddingVertical: 0,
      paddingHorizontal: isXS ? 3 : 5,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      borderWidth: 1,
      overflow: 'hidden',
    },
    chipIcon: { width: 11, alignItems: 'center' },
    chipName: { fontSize: isXS ? 9 : 10, lineHeight: isXS ? 11 : 12, flex: 1 },

    playhead: {
      position: 'absolute',
      top: 0, bottom: 0,
      left: gutterW,
      width: 0,
      pointerEvents: 'none',
    },
    playheadGlow: {
      position: 'absolute',
      top: -8, bottom: -8,
      width: 60,
      marginLeft: -30,
    },
    playheadCore: {
      position: 'absolute',
      top: -4, bottom: -4,
      width: 2,
      marginLeft: -1,
      borderRadius: 1,
      backgroundColor: accent,
    },

    completionBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: 5,
      paddingHorizontal: 12,
      borderRadius: 999,
      backgroundColor: `${TINT.green}14`,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${TINT.green}44`,
      alignSelf: 'center',
    },
  }), [colors, isXS, accent, gutterW]);

  const usableWidth = Math.max(0, gridWidth - gutterW);
  const playheadTranslate = playhead.interpolate({
    inputRange: [0, 1],
    outputRange: [0, usableWidth],
  });

  return (
    <View style={styles.wrap}>
      <LinearGradient
        colors={[`${accent}14`, 'transparent']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.inner}>
        <View style={styles.headerRow}>
          <View style={styles.headerCopy}>
            <Text size="xs" weight="bold" color="secondary" style={styles.eyebrow}>
              ② A whole week
            </Text>
            <Text weight="extraBold" style={styles.title}>
              Now stack a few.
            </Text>
            <Text size="n" color="secondary" style={styles.subtitle}>
              A few thoughtful tasks — and your week starts to run itself.
            </Text>
          </View>
        </View>

        <View style={styles.calendarCard}>
          <View style={styles.dayHeaderRow}>
            <View style={styles.timeGutter} />
            {DAYS.map((d) => (
              <View key={d} style={styles.dayHeaderCell}>
                <Text size="xxs" weight="bold" color="tertiary" style={styles.dayLabel}>
                  {d}
                </Text>
              </View>
            ))}
          </View>

          <View
            style={styles.gridArea}
            onLayout={(e) => setGridWidth(e.nativeEvent.layout.width)}
          >
            {ROWS.map((row, rIdx) => (
              <View key={row.label} style={styles.gridRow}>
                <View style={styles.timeLabelCell}>
                  <Text size="xxs" color="tertiary" style={styles.timeLabel}>
                    {row.label}
                  </Text>
                </View>
                {DAYS.map((_, cIdx) => {
                  const taskIdx = TASKS.findIndex(t => t.col === cIdx && t.row === rIdx);
                  const task = taskIdx >= 0 ? TASKS[taskIdx] : null;
                  const type = task ? TYPES[task.type] : null;
                  const a = taskIdx >= 0 ? taskAnims[taskIdx] : null;

                  return (
                    <View key={cIdx} style={styles.dayCell}>
                      {task && (
                        <Animated.View
                          style={[
                            styles.chip,
                            {
                              backgroundColor: `${type.tint}1f`,
                              borderColor: `${type.tint}55`,
                              opacity: a.enter,
                              transform: [
                                { translateY: a.enter.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) },
                                { scale: a.enter.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1] }) },
                              ],
                              shadowColor: type.tint,
                              shadowOpacity: a.glow.interpolate({ inputRange: [0, 1], outputRange: [0, 0.6] }),
                              shadowRadius: 10,
                              shadowOffset: { width: 0, height: 0 },
                            },
                          ]}
                        >
                          <View style={styles.chipIcon}>
                            <Animated.View style={{ opacity: a.delivered.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }) }}>
                              <Icon name={type.icon} size={8} color={type.tint} solid />
                            </Animated.View>
                            <Animated.View
                              style={{
                                position: 'absolute',
                                opacity: a.delivered,
                                transform: [{ scale: a.delivered }],
                              }}
                            >
                              <Icon name="check" size={8} color={TINT.green} solid />
                            </Animated.View>
                          </View>
                          {!isXS && (
                            <Text
                              size="xxs"
                              weight="bold"
                              numberOfLines={1}
                              style={[styles.chipName, { color: type.tint }]}
                            >
                              {type.name}
                            </Text>
                          )}
                        </Animated.View>
                      )}
                    </View>
                  );
                })}
              </View>
            ))}

            <Animated.View
              style={[
                styles.playhead,
                {
                  opacity: playheadOpacity,
                  transform: [{ translateX: playheadTranslate }],
                },
              ]}
            >
              <View style={styles.playheadGlow}>
                <LinearGradient
                  colors={['transparent', `${accent}33`, `${accent}66`, `${accent}33`, 'transparent']}
                  start={{ x: 0, y: 0.5 }}
                  end={{ x: 1, y: 0.5 }}
                  style={StyleSheet.absoluteFill}
                />
              </View>
              <View style={styles.playheadCore} />
            </Animated.View>
          </View>

          <Animated.View
            style={[
              styles.completionBanner,
              {
                opacity: completionOp,
                transform: [{ translateY: completionOp.interpolate({ inputRange: [0, 1], outputRange: [4, 0] }) }],
              },
            ]}
          >
            <Icon name="circle-check" size={10} color={TINT.green} solid />
            <Text size="xxs" weight="bold" style={{ color: TINT.green }}>
              All {TASKS.length} ran on time. Your week, handled.
            </Text>
          </Animated.View>
        </View>
      </View>
    </View>
  );
}

const DEMO_CAROUSEL_GAP = 16;

/** Horizontal snap carousel for the two animated demo cards (One task / A whole week). */
function TasksHeroDemoCarousel({ cardWidth }) {
  const { colors } = useTheme();
  const scrollRef = useRef(null);
  const [page, setPage] = useState(0);
  const stepX = cardWidth + DEMO_CAROUSEL_GAP;

  /** Derive which panel is “current” from layout so arrows match real max scroll (not only stepX math). */
  const syncPageFromScrollEvent = useCallback((e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const maxX = Math.max(0, contentSize.width - layoutMeasurement.width);
    const x = contentOffset.x;
    if (maxX < 6) {
      setPage(0);
      return;
    }
    setPage(x >= maxX / 2 ? 1 : 0);
  }, []);

  const onScroll = useCallback((e) => {
    syncPageFromScrollEvent(e);
  }, [syncPageFromScrollEvent]);

  const scrollToPage = useCallback((index) => {
    const clamped = Math.min(1, Math.max(0, index));
    scrollRef.current?.scrollTo({ x: clamped * stepX, animated: true });
    setPage(clamped);
  }, [stepX]);

  const arrowStyles = useMemo(() => StyleSheet.create({
    overlay: {
      ...StyleSheet.absoluteFillObject,
      pointerEvents: 'box-none',
      zIndex: 4,
    },
    fab: {
      position: 'absolute',
      top: '34%',
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: StyleSheet.hairlineWidth,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.14,
      shadowRadius: 4,
      elevation: 3,
    },
    fabLeft: { left: 6 },
    fabRight: { right: 6 },
  }), []);

  return (
    <View style={{ position: 'relative', width: '100%' }}>
      <ScrollView
        ref={scrollRef}
        horizontal
        // A full-width horizontal UIScrollView wins the responder before the
        // parent Tasks scroller can recognize vertical intent. Keep native
        // navigation on the visible previous/next buttons so the catalog
        // below remains reachable with a normal vertical swipe.
        scrollEnabled={Platform.OS === 'web'}
        pointerEvents={Platform.OS === 'web' ? 'auto' : 'none'}
        showsHorizontalScrollIndicator={false}
        decelerationRate="fast"
        snapToInterval={stepX}
        snapToAlignment="start"
        contentContainerStyle={{
          flexDirection: 'row',
          gap: DEMO_CAROUSEL_GAP,
          paddingRight: DEMO_CAROUSEL_GAP,
        }}
        onScroll={onScroll}
        onMomentumScrollEnd={syncPageFromScrollEvent}
        onScrollEndDrag={syncPageFromScrollEvent}
        scrollEventThrottle={16}
      >
        <View style={{ width: cardWidth }}>
          <WatchItHappenHero />
        </View>
        <View style={{ width: cardWidth }}>
          <YourWeekHandledHero />
        </View>
      </ScrollView>

      <View style={arrowStyles.overlay} pointerEvents="box-none">
        {page === 1 ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Previous demo"
            onPress={() => scrollToPage(0)}
            style={({ hovered, pressed }) => [
              arrowStyles.fab,
              arrowStyles.fabLeft,
              {
                backgroundColor: colors.solid_color_block,
                borderColor: hovered ? colors.text_color_tertiary : colors.border_color_secondary,
                opacity: pressed ? 0.88 : 1,
              },
            ]}
          >
            <Icon name="chevron-left" size={14} color={colors.text_color_primary} solid />
          </Pressable>
        ) : null}
        {page === 0 ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Next demo"
            onPress={() => scrollToPage(1)}
            style={({ hovered, pressed }) => [
              arrowStyles.fab,
              arrowStyles.fabRight,
              {
                backgroundColor: colors.solid_color_block,
                borderColor: hovered ? colors.text_color_tertiary : colors.border_color_secondary,
                opacity: pressed ? 0.88 : 1,
              },
            ]}
          >
            <Icon name="chevron-right" size={14} color={colors.text_color_primary} solid />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

export default function TasksHero() {
  const { isMD, isXS, windowWidth } = useResponsiveness();
  const [contentWidth, setContentWidth] = useState(0);
  const measureContent = useCallback(event => setContentWidth(event.nativeEvent.layout.width), []);

  // macOS does not need a touch carousel. Measure the available page width
  // (including sidebar changes), and keep both demos in normal vertical flow.
  if (Platform.OS === 'macos') {
    const stacked = contentWidth < 760;
    return (
      <View onLayout={measureContent} style={{ width: '100%', flexDirection: stacked ? 'column' : 'row', gap: 16 }}>
        <View style={stacked ? { width: '100%' } : { flex: 1, minWidth: 0 }}>
          <WatchItHappenHero />
        </View>
        <View style={stacked ? { width: '100%' } : { flex: 1, minWidth: 0 }}>
          <YourWeekHandledHero />
        </View>
      </View>
    );
  }

  // Side-by-side reads great with room to breathe — but the moment we
  // get into "tablet-or-narrow-desktop-with-sidebar" territory the two
  // demo panels squash. Below isLG we switch to a snap-to-card
  // horizontal scroller so each panel keeps its narrative full size.
  const isCarousel = isMD;
  const cardWidth = isXS
    ? Math.max(300, Math.min(windowWidth - 56, 380))
    : 380;

  if (isCarousel) {
    return <TasksHeroDemoCarousel cardWidth={cardWidth} />;
  }

  return (
    <View style={{ flexDirection: 'row', gap: 16 }}>
      <View style={{ flex: 1, minWidth: 0 }}>
        <WatchItHappenHero />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <YourWeekHandledHero />
      </View>
    </View>
  );
}

export {
  WatchItHappenHero,
  YourWeekHandledHero,
};
